import {isFilled} from '@prismicio/client'
import {PrismicNextImage} from '@prismicio/next/pages'
import cn from 'classnames'
import dayjs from 'dayjs'
import Link from 'next/link'
import {useLinkResolver} from 'prismicio'
import type {CarouselSliceEventHeroPrimaryEventsItem} from 'prismicio-types'
import {useGetEventStatus} from 'src/common/hooks/useGetEventStatus'
import {Icon} from 'src/common/Icon'
import {formatEventTypeDisplay} from 'src/common/utils/eventRoutes'
import {Tag} from 'src/Event/components/Hero/components/Tag'

export const EventSlide = ({
  event,
  index,
  activeIndex,
}: {
  event: CarouselSliceEventHeroPrimaryEventsItem
  index: number
  activeIndex: number
}) => {
  const eventData = isFilled.contentRelationship(event.event) && event.event.data ? event.event.data : null
  const isOnDemand = eventData?.event_type === 'On demand'

  const eventStatus = useGetEventStatus({
    eventDate: eventData?.event_date,
    eventFinishDate: eventData?.event_finish_date,
    eventType: isFilled.select(eventData?.event_type) ? eventData?.event_type : null,
  })

  const {linkResolver} = useLinkResolver()

  if (!eventData) {
    return null
  }

  const isPastEvent = eventStatus?.label === 'PAST EVENT'
  const date = isFilled.timestamp(eventData.event_date) ? dayjs(eventData.event_date).format('MMMM DD, YYYY') : null
  const time = isFilled.timestamp(eventData.event_date) ? dayjs(eventData.event_date).format('h A') : null

  const language = isFilled.keyText(eventData.language) ? eventData.language : null

  return (
    <Link href={linkResolver(event.event)}>
      <div className="relative w-full text-clip rounded-[8px] border border-solid border-black/10 bg-moss-white">
        <div
          className={cn(
            'box-border flex w-full flex-col items-start gap-6 text-clip rounded-[inherit] p-6 transition-opacity duration-300',
            'lg:flex-row lg:justify-center lg:gap-8 lg:p-8',
            {
              'lg:opacity-30': index !== activeIndex,
            },
          )}
        >
          <div className="flex min-w-0 flex-col gap-4 text-clip lg:h-[320px] lg:flex-1 lg:gap-6">
            <div className="flex flex-1 flex-col gap-4">
              <div className="flex flex-wrap items-center gap-4">
                <div className="flex items-center gap-2">
                  {!isOnDemand && eventStatus && <Tag variant={eventStatus.variant}>{eventStatus.label}</Tag>}
                  {isFilled.select(eventData.event_type) && (isOnDemand || !isPastEvent) && (
                    <Tag variant="secondary">{formatEventTypeDisplay(eventData.event_type)}</Tag>
                  )}
                </div>

                <div className="flex items-center gap-3 lg:gap-2">
                  {date && (
                    <div className="flex flex-col justify-center">
                      <p className="text-detail text-moss-grey-400">{date}</p>
                    </div>
                  )}
                  {!isPastEvent && date && time && (
                    <div className="h-[6px] w-[6px] rounded-[1px] bg-moss-grey-200 lg:size-1" />
                  )}
                  {!isPastEvent && time && (
                    <div className="flex flex-col justify-center">
                      <p className="text-detail text-moss-grey-400">{time}</p>
                    </div>
                  )}
                  {!isPastEvent && time && language && (
                    <div className="h-[6px] w-[6px] rounded-[1px] bg-moss-grey-200 lg:size-1" />
                  )}
                  {!isPastEvent && language && (
                    <div className="flex flex-col justify-center">
                      <p className="text-detail text-moss-grey-400">{language}</p>
                    </div>
                  )}
                </div>
              </div>

              {isFilled.keyText(eventData.title) && (
                <h2 className="text-24 font-medium leading-normal text-moss-black">{eventData.title}</h2>
              )}

              {isFilled.keyText(eventData.meta_description) && (
                <p className="text-tg-body-m w-full text-moss-grey-550 lg:w-[302px]">{eventData.meta_description}</p>
              )}
            </div>

            <div className="group relative hidden h-[38px] w-fit cursor-pointer items-center gap-[14px] rounded-[4px] bg-moss-white pl-[14px] pr-[7px] hover:bg-moss-grey-100 lg:flex">
              <span className="text-10 font-semibold uppercase leading-normal tracking-[0.6px] text-moss-black">
                {isFilled.keyText(event.cta_button_label) && event.cta_button_label}
              </span>
              <div className="flex size-6 items-center justify-center rounded-[2px] bg-green-neon group-hover:bg-green-neon/[80%]">
                <Icon name="arrow" className="h-2 w-2.5 text-green-dark" />
              </div>
              <div className="pointer-events-none absolute inset-0 rounded-[4px] border border-moss-black/[10%]" />
            </div>
          </div>

          {isFilled.image(eventData.hero_image) && (
            <div className="aspect-[556/320] w-full shrink-0 rounded-[4px] lg:h-[320px] lg:w-auto">
              <PrismicNextImage field={eventData.hero_image} className="size-full rounded-[4px] object-cover" />
            </div>
          )}

          <div className="group relative flex h-[38px] w-fit cursor-pointer items-center gap-[14px] rounded-[4px] bg-moss-white pl-[14px] pr-[7px] hover:bg-moss-grey-100 lg:hidden">
            <span className="text-10 font-semibold uppercase leading-normal tracking-[0.6px] text-moss-black">
              {isFilled.keyText(event.cta_button_label) && event.cta_button_label}
            </span>
            <div className="flex size-6 items-center justify-center rounded-[2px] bg-green-neon group-hover:bg-green-neon/[80%]">
              <Icon name="arrow" className="h-2 w-2.5 text-green-dark" />
            </div>
            <div className="pointer-events-none absolute inset-0 rounded-[4px] border border-moss-black/[10%]" />
          </div>
        </div>
      </div>
    </Link>
  )
}
